import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CCard,
  CCardGroup,
} from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import EmailTemplateForm from "../emailTemplateForm";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { emailTemplate } from "@/constants/translation/emailTemplate.translation";
import EmailTemplateValidationClass from "@/helpers/validations/emailTemplate";
import { messages } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import { EmailTemplateService } from "@/api/emailTemplateService";
import constants from "@/libs/constant";

/**
 * Component for adding an email template.
 * @returns {JSX.Element} The rendered AddEmailTemplate component.
 */
const AddEmailTemplate = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    // Fetch languages on component mount
    const fetchLanguages = async () => {
      try {
        // Fetch languages from API
        const languagesResponse = await TranslationService.getLanguages();
        
        // Handle the actual API response structure: { message: '...', data: { "0": {...}, "1": {...} } }
        const languagesData = languagesResponse.data || languagesResponse;
        let languagesList = [];
        
        if (Array.isArray(languagesData)) {
          languagesList = languagesData;
        } else if (languagesData && typeof languagesData === constants.dataTypes.object) {
          // Handle object with numeric keys like { "0": {...}, "1": {...} }
          // This is the actual structure from your API
          languagesList = Object.values(languagesData).map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code,
            isDefault: lang.isDefault,
            displayName: lang.name // Use the actual language name
          }));
          
          // Sort languages: English first, then others alphabetically
          languagesList.sort((a, b) => {
            if (a.name.toLowerCase() === constants.defaultLanguage) return -1;
            if (b.name.toLowerCase() === constants.defaultLanguage) return 1;
            return a.name.localeCompare(b.name);
          });
        }
        
        setLanguages(languagesList);
      } catch (error) {
        toast.error(t(messages.toast.translation.languagesFetchFailed));
        console.error("Fetch languages error:", error);
      }
    };
    
    fetchLanguages();
  }, []);

  const handleAddEmailTemplate = async (values) => {
    
    setIsLoading(true);
    try {
      // Transform the data structure to match API expectations
      const apiData = {
        slug: values.slug,
        parameter: values.parameter,
        templates: values.email_templates.map(template => ({
          language_id: template.language_id,
          title: template.title,
          subject: template.subject,
          content: template.content
        }))
      };
      
      const response = await EmailTemplateService.add(apiData);
      
      toast.success(response.message || t(messages.toast.emailTemplate.added));
      navigate(masters.emailTemplate.list);
    } catch (error) {
      console.error("Add email template error:", error);
      toast.error(t(messages.toast.emailTemplate.addFailed));
    } finally {
      setIsLoading(false);
    }
  };

  // Initial values for the email template form
  const initialValues = useMemo(() => {

    return {
      slug: "",
      parameter: "",
      email_templates: languages.map((lang) => ({
        language: lang.name,
        language_id: lang.id,
        title: "",
        subject: "",
        content: "",
      })),
    };
  }, [languages]);

  // Validation schema for the email template form
  const validationSchema = useMemo(
    () => EmailTemplateValidationClass.emailTemplateValidation(),
    []
  );

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(emailTemplate.add.main_heading)}</h1>
        <EmailTemplateForm
          initialValues={initialValues}
          onSubmit={handleAddEmailTemplate}
          emailTemplatedLoaded={isLoading}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

// Static data that would have been passed via getStaticProps
AddEmailTemplate.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.emailTemplate?.list,
    pathname: masters.emailTemplate.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.emailTemplate?.add,
    pathname: masters.emailTemplate.add,
    active: true,
  },
];

export default AddEmailTemplate;